import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useCurrentuser } from '@uplift/contexts';
import { useDownBreakpoint, useL10n } from '@uplift/hooks';
import { CalendarDateService, DateObject } from '@uplift/services';
import { Button, useScheduler, useSchedulerSlots } from '@uplift/ui-v2';
import { useCallback, useMemo } from 'react';
import { useAlert } from 'react-alert';
import { useSelectedProviderStyles } from '../../styles';

export type SchedulerRawProps = {
  slots: DateObject[];
  isSmall: boolean; // note: sending isSmall through props prevents the triad variant from flickering on mobile view
} & SchedulerProps;

export type SchedulerProps = {
  medicalRole: string;
  daysAheadLimit: number;
  providerId: string;
  excludeProviderIds?: string[];
  onScheduleSession: (selectedSlot: DateObject) => void;
  appointment?: {
    providerUuid: string;
    patientUuid: string;
    fromDatetime: DateObject;
    toDatetime: DateObject;
  };
  isPreview?: boolean;
  forceMobile?: boolean;
};

const SchedulerRaw: React.VFC<SchedulerRawProps> = ({
  medicalRole,
  providerId,
  onScheduleSession,
  appointment,
  slots,
  isSmall,
  daysAheadLimit,
  isPreview = false,
  forceMobile
}) => {
  const l10n = useL10n();
  const { classes } = useSelectedProviderStyles();
  const { classes: schedulerClasses } = useSchedulerStyles({ forceMobile });
  const alert = useAlert();

  const initialSelectedSlot = useMemo(() => {
    if (appointment?.fromDatetime && appointment?.providerUuid === providerId) {
      return CalendarDateService.date(appointment.fromDatetime);
    }
  }, [ appointment?.fromDatetime, appointment?.providerUuid, providerId ]);

  const onDisabledForwardClickHandler = useCallback(() => {
    const limit = daysAheadLimit <= 31
      ? CalendarDateService.date().add(daysAheadLimit, 'days').fromNow(true)
      : `${daysAheadLimit} days`;

    alert.info(l10n.matching.selectedProvider.limitAlert(limit));
  }, [ alert, daysAheadLimit, l10n.matching.selectedProvider ]);

  const { selectDate, timeslots, timezoneSelect, onScheduleSessionClick } = useScheduler({
    timeslots: slots,
    isTriad: !isSmall,
    daysAheadLimit,
    onScheduleSession: onScheduleSession,
    initialSelectedSlot: initialSelectedSlot,
    onDisabledForwardClick: onDisabledForwardClickHandler,
    disableTimezoneSelect: isPreview,
  });

  const actions = useMemo(() => {
    if (isSmall) {
      return (
        <Box mt={2} textAlign="center">
          {timezoneSelect}
        </Box>
      );
    }

    return (
      <>
        <Box px={1} mt={2}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={onScheduleSessionClick}
            disabled={isPreview}
          >
            {l10n.matching.selectedProvider.scheduleSession}
          </Button>
        </Box>
        <Box mt={2} textAlign="center">
          {timezoneSelect}
        </Box>
      </>
    );
  }, [ isPreview, isSmall, l10n.matching.selectedProvider.scheduleSession, onScheduleSessionClick, timezoneSelect ]);

  return (
    <Box className={schedulerClasses.scheduler}>
      <Box mb={{ xs: 1, sm: 2 }} textAlign="center">
        <Typography variant="h6" color="textPrimary" className={classes.fontSize18}>
          {medicalRole}
        </Typography>
      </Box>
      {selectDate}
      {timeslots}
      {actions}
    </Box>
  );
};

const useSchedulerStyles = makeStyles<{ forceMobile?: boolean }>({ name: 'Scheduler' })((theme, { forceMobile }) => ({
  scheduler: ({
    background: theme.palette.grey[100],
    padding: theme.spacing(5, 1, 7),

    ...(!forceMobile && {
      [theme.breakpoints.up('sm')]: {
        background: theme.palette.background.paper,
        padding: theme.spacing(3, 2, 2),
        border: `1px solid ${theme.palette.divider}`,
      }
    })
  }),
}));

export const Scheduler: React.VFC<SchedulerProps> = props => {
  const me = useCurrentuser();
  const isSmDown = useDownBreakpoint('sm');
  const { slots, isLoading } = useSchedulerSlots({
    providerId: props.providerId,
    daysAheadLimit: props.daysAheadLimit,
    patientId: props.isPreview || props.providerId === me?.id ? undefined : me?.id,
    excludeProviderIds: props.excludeProviderIds
  });

  if (isLoading) {
    // heights based on the scheduler rendered component height
    return (
      <Skeleton width="100%" variant="rectangular" height={isSmDown ? '278px' : '556px'} />
    );
  }

  return <SchedulerRaw {...props} slots={slots} isSmall={isSmDown} />;
};
